/**
 * SelfServeHeader — Mirrors the portal Header component styling
 * for the self-serve flow (no auth context required).
 * Matches: bg-white/95 backdrop-blur, border-b, sticky top-0 z-50, h-16
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard' },
  { path: '/campaign', label: 'Campaigns' },
];

export default function SelfServeHeader({ businessName, user, showNav = true, showAuth = true, returnTo = null }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/campaign') return location.pathname.startsWith('/campaign/');
    return location.pathname === path;
  };

  return (
    <header className="bg-card/95 backdrop-blur-sm border-b border-border sticky top-0 z-50 shadow-[0_1px_3px_0_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-4 md:px-6 h-14 sm:h-16">
        {/* Back link (when navigated from leads) */}
        {returnTo && (
          <button
            onClick={() => navigate(returnTo)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0 mr-1"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to Leads</span>
          </button>
        )}

        {/* Logo */}
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="cursor-pointer shrink-0 flex items-center gap-2"
            onClick={() => navigate('/dashboard')}
          >
            <img
              src="/assets/logos/cornercast-logo-primary.svg"
              alt="CornerCast"
              className="h-6 sm:h-7"
              onError={(e) => {
                // Fallback if logo doesn't exist
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="items-center gap-2 hidden" style={{ display: 'none' }}>
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-coral" />
              <span className="font-bold text-sm sm:text-base">CornerCast</span>
            </div>
          </div>

          {businessName && (
            <>
              <span className="text-border text-lg select-none hidden sm:inline">/</span>
              <span className="text-xs sm:text-sm font-semibold text-foreground truncate max-w-[120px] sm:max-w-none hidden sm:inline">{businessName}</span>
            </>
          )}
        </div>

        {/* Navigation -- hidden on mobile */}
        {showNav && (
          <nav className="flex-1 justify-center hidden md:flex">
            <div className="flex gap-1">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
                    isActive(item.path)
                      ? 'text-foreground border-b-2 border-coral rounded-none'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-lg'
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </nav>
        )}

        {/* Right section */}
        <div className="flex items-center gap-2 shrink-0">
          {import.meta.env.VITE_ENABLE_MOCK_GENERATION === 'true' && (
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-coral/10 text-coral px-2 py-0.5 rounded-full hidden sm:inline">
              Demo
            </span>
          )}

          {user ? (
            <Avatar className="h-8 w-8 cursor-pointer bg-coral text-white hover:opacity-90 transition-opacity">
              <AvatarFallback className="bg-coral text-white text-sm font-medium">
                {(user.name || user.email || 'U').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          ) : showAuth ? (
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
              Sign in
            </Button>
          ) : null}
        </div>
      </div>
    </header>
  );
}
